'use client';

import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { CheckIcon, XIcon } from '@phosphor-icons/react';

/**
 * Parses a pt-BR money input ("2.500,00", "2500", "R$ 900") into a number, null when
 * invalid. `0` is a valid parse — committing it clears the limit, same as an empty field.
 */
export function parseLimitInput(raw: string): number | null {
  if (raw.includes('-') || raw.includes('−')) return null;
  const cleaned = raw.replace(/[^\d.,]/g, '').trim();
  // At least one digit — punctuation-only input ('.', ',') would otherwise
  // normalize to '' and Number('') === 0, silently reading as "remove".
  if (!/\d/.test(cleaned)) return null;
  const normalized = cleaned.replace(/\./g, '').replace(',', '.');
  const value = Number(normalized);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100) / 100;
}

function InvalidValueAlert({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <span role="alert" className="sr-only">
      Valor inválido.
    </span>
  );
}

/**
 * The one inline monthly-limit editor: pt-BR decimal input seeded by the host
 * (current limit, or the 3-month average for a first limit), Enter/Escape,
 * check/x buttons. Used by the Categorias rows (root + child lines), the
 * transações category drawer and the Category Hub's Limite view. `onCommit`
 * only fires on valid input (empty or 0 = clear the limit); invalid input keeps
 * the editor open and marks the field invalid.
 */
export function InlineLimitEditor({
  initialValue,
  saving = false,
  onCommit,
  onCancel,
  size = 'md',
}: {
  initialValue: number | null;
  saving?: boolean;
  onCommit: (value: number | null) => void;
  onCancel: () => void;
  /** `sm` matches the child-line / drawer-row density. */
  size?: 'md' | 'sm';
}) {
  const [draft, setDraft] = useState(() =>
    initialValue != null ? initialValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '',
  );
  const [invalid, setInvalid] = useState(false);

  const commit = () => {
    const parsed = parseLimitInput(draft);
    if (draft.trim() !== '' && parsed == null) {
      setInvalid(true); // keep the editor open, but say so instead of silently ignoring
      return;
    }
    onCommit(parsed === 0 ? null : parsed); // 0 removes the limit, same contract as empty
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInvalid(false);
    setDraft(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') onCancel();
  };

  const button = size === 'sm' ? 'size-7' : 'size-8';

  return (
    <span className="flex items-center gap-1.5">
      <input
        autoFocus
        inputMode="decimal"
        value={draft}
        placeholder="0,00"
        disabled={saving}
        aria-invalid={invalid || undefined}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={`w-26 rounded-md border border-border-brand bg-surface px-2.5 shadow-xs ${
          size === 'sm' ? 'py-1' : 'py-1.5'
        } text-right font-mono text-base tabular-nums outline-none focus-visible:ring-[3px] focus-visible:ring-brand-200/60 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 sm:text-sm`}
      />
      <InvalidValueAlert show={invalid} />
      <button
        type="button"
        aria-label="Salvar limite"
        onClick={commit}
        disabled={saving}
        className={`grid ${button} shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground`}
      >
        <CheckIcon className="size-3.5" />
      </button>
      <button
        type="button"
        aria-label="Cancelar edição do limite"
        onClick={onCancel}
        disabled={saving}
        className={`grid ${button} shrink-0 place-items-center rounded-lg border border-border-subtle text-text-secondary`}
      >
        <XIcon className="size-3.5" />
      </button>
    </span>
  );
}
